import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  Automation,
  AutomationExecution,
  AutomationExecutionStep,
} from "./types";

async function getHeaders(crmId?: string): Promise<Record<string, string>> {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  const activeCrmId =
    crmId ||
    localStorage.getItem("dotus_active_crm_id") ||
    "fe386f56-b27f-446f-86ec-39a03b64add1";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (activeCrmId) {
    headers["x-crm-id"] = activeCrmId;
  }

  return headers;
}

export async function fetchAutomations(
  crmId?: string,
): Promise<{ success: boolean; data: Automation[] }> {
  const headers = await getHeaders(crmId);
  const res = await fetch("/api/automations", { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Erro ao buscar automações (${res.status})`);
  }
  return res.json();
}

export async function fetchAutomation(
  id: number | string,
  crmId?: string,
): Promise<{ success: boolean; data: Automation }> {
  const headers = await getHeaders(crmId);
  const res = await fetch(`/api/automations?id=${id}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || `Erro ao carregar automação (${res.status})`,
    );
  }
  const json = await res.json();
  const item = Array.isArray(json.data) ? json.data[0] : json.data;
  return { success: true, data: item };
}

export async function createAutomation(
  payload: Partial<Automation> & { passos?: any[] },
  crmId?: string,
): Promise<{ success: boolean; data: Automation }> {
  const headers = await getHeaders(crmId);
  const res = await fetch("/api/automations", {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Erro ao criar automação (${res.status})`);
  }
  return res.json();
}

export async function updateAutomation(
  id: number | string,
  payload: Partial<Automation> & { passos?: any[] },
  crmId?: string,
): Promise<{ success: boolean; data: Automation }> {
  const headers = await getHeaders(crmId);
  const res = await fetch(`/api/automations?id=${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || `Erro ao atualizar automação (${res.status})`,
    );
  }
  return res.json();
}

export async function toggleAutomation(
  id: number | string,
  crmId?: string,
): Promise<{ success: boolean; data: Automation }> {
  const headers = await getHeaders(crmId);
  const res = await fetch(`/api/automations?action=toggle&id=${id}`, {
    method: "POST",
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Erro ao alternar status (${res.status})`);
  }
  return res.json();
}

export async function deleteAutomation(
  id: number | string,
  crmId?: string,
): Promise<{ success: boolean }> {
  const headers = await getHeaders(crmId);
  const res = await fetch(`/api/automations?id=${id}`, {
    method: "DELETE",
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Erro ao excluir automação (${res.status})`);
  }
  return res.json();
}

export async function fetchExecutions(
  params: { automacao_id?: number | string; limit?: number } = {},
  crmId?: string,
): Promise<{ success: boolean; data: AutomationExecution[] }> {
  const headers = await getHeaders(crmId);
  const query = new URLSearchParams();
  query.set("action", "execucoes");
  if (params.automacao_id)
    query.set("automacao_id", String(params.automacao_id));
  if (params.limit) query.set("limit", String(params.limit));

  const res = await fetch(`/api/automations?${query.toString()}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Erro ao buscar execuções (${res.status})`);
  }
  return res.json();
}

export async function fetchExecutionSteps(
  execucao_id: number | string,
  crmId?: string,
): Promise<{ success: boolean; data: AutomationExecutionStep[] }> {
  const headers = await getHeaders(crmId);
  const res = await fetch(
    `/api/automations?action=passos_execucao&execucao_id=${execucao_id}`,
    { headers },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.message || `Erro ao buscar passos da execução (${res.status})`,
    );
  }
  return res.json();
}

export async function triggerManualTest(
  params: {
    item: string;
    evento: string;
    registro_id: string;
    dados: Record<string, any>;
  },
  crmId?: string,
): Promise<{ success: boolean; message: string }> {
  const headers = await getHeaders(crmId);
  const activeCrmId =
    crmId ||
    localStorage.getItem("dotus_active_crm_id") ||
    "fe386f56-b27f-446f-86ec-39a03b64add1";

  const res = await fetch("/api/automations?action=fire", {
    method: "POST",
    headers,
    body: JSON.stringify({
      crm_id: activeCrmId,
      ...params,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Erro ao disparar teste (${res.status})`);
  }
  return res.json();
}
